import type { LcOrderItem } from '../../../services/api/wallet';
import type { TimeRange } from '../../../utils/earningsUtils';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function startOfRange(timeRange: TimeRange, now: Date): Date | null {
  if (timeRange === 'ALL') return null;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (timeRange === '1M') {
    start.setDate(start.getDate() - 27);
    return start;
  }
  if (timeRange === '3M') {
    start.setMonth(start.getMonth() - 2);
    return start;
  }
  if (timeRange === '1Y') {
    start.setMonth(start.getMonth() - 11);
    return start;
  }
  return null;
}

export function filterLcOrdersByTimeRange(
  orders: LcOrderItem[],
  timeRange: TimeRange,
  now = new Date(),
): LcOrderItem[] {
  const rangeStart = startOfRange(timeRange, now);
  return orders.filter((order) => {
    const completedAt = new Date(order.completedAt);
    if (completedAt > now) return false;
    if (!rangeStart) return true;
    return completedAt >= rangeStart;
  });
}

export function sumLcOrderCash(orders: LcOrderItem[]): number {
  return orders.reduce((sum, order) => sum + order.cash, 0);
}

export function mergeLcOrdersIntoChartData(
  chartData: { month: string; value: number }[],
  lcOrders: LcOrderItem[],
  timeRange: TimeRange,
): { month: string; value: number }[] {
  const orders = filterLcOrdersByTimeRange(lcOrders, timeRange);
  if (orders.length === 0) return chartData;

  if (timeRange === 'ALL') {
    const lcByLabel = new Map<string, number>();
    for (const order of orders) {
      const d = new Date(order.completedAt);
      const label = `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
      lcByLabel.set(label, (lcByLabel.get(label) ?? 0) + order.cash);
    }
    return chartData.map((point) => ({
      month: point.month,
      value: point.value + (lcByLabel.get(point.month) ?? 0),
    }));
  }

  if (timeRange === '1M') {
    const result = chartData.map((point) => ({ ...point }));
    const now = new Date();
    for (const order of orders) {
      const d = new Date(order.completedAt);
      const daysAgo = Math.floor((now.getTime() - d.getTime()) / 86400000);
      let fromIndex = 0;
      if (daysAgo <= 6) fromIndex = 3;
      else if (daysAgo <= 13) fromIndex = 2;
      else if (daysAgo <= 20) fromIndex = 1;
      else if (daysAgo <= 27) fromIndex = 0;
      else continue;

      for (let i = fromIndex; i < result.length; i++) {
        result[i].value += order.cash;
      }
    }
    return result;
  }

  const lcByLabel = new Map<string, number>();
  for (const order of orders) {
    const d = new Date(order.completedAt);
    const label =
      timeRange === '1Y' ? `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}` : MONTH_LABELS[d.getMonth()];
    lcByLabel.set(label, (lcByLabel.get(label) ?? 0) + order.cash);
  }

  return chartData.map((point) => ({
    month: point.month,
    value: point.value + (lcByLabel.get(point.month) ?? 0),
  }));
}
